import type { McpConsentRequest, McpScope } from '@simplemon/lib';
import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  consentRequestId,
  McpSignInRequiredError,
  mcpClient,
  oauthLoginPath,
} from '@/lib/mcp-client';

export function OAuthConsentPage() {
  const [params] = useSearchParams();
  const requestId = consentRequestId(params.toString());
  const [request, setRequest] = useState<McpConsentRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signInRequired, setSignInRequired] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [access, setAccess] = useState<'read' | 'write' | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRequest(null);
    setOrganizationId('');
    setAccess('');
    setSignInRequired(false);
    try {
      if (!requestId)
        throw new Error(
          'This connection request is invalid. Start a new connection from your client.',
        );
      setRequest(await mcpClient.consent(requestId));
    } catch (cause) {
      if (cause instanceof McpSignInRequiredError) setSignInRequired(true);
      else setError(cause instanceof Error ? cause.message : 'Could not load connection request.');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function decide(decision: 'approve' | 'deny') {
    if (!request || !requestId) return;
    setSubmitting(true);
    setError(null);
    try {
      const scopes: McpScope[] =
        access === 'write' ? ['monitors:read', 'monitors:write'] : ['monitors:read'];
      const redirect = await mcpClient.decide(requestId, {
        csrfToken: request.csrfToken,
        decision,
        ...(decision === 'approve' ? { organizationId, scopes } : {}),
      });
      window.location.assign(redirect);
    } catch (cause) {
      if (cause instanceof McpSignInRequiredError) setSignInRequired(true);
      setError(cause instanceof Error ? cause.message : 'Could not submit your decision.');
      // Consent nonces are single-use. Reload before retrying either decision.
      setRequest(null);
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-12 sm:px-6">
      <div className="mx-auto max-w-xl">
        <Link
          className="text-xl font-bold text-gray-900 focus-visible:outline-2 focus-visible:outline-blue-600"
          to="/"
        >
          Valvo.io
        </Link>
        <Card className="mt-6">
          <CardHeader>
            <h1 className="text-2xl font-semibold text-gray-900">Connect a client</h1>
            <p className="text-sm text-gray-600">Choose what this client can access in Valvo.</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <output>Loading connection request…</output>
            ) : signInRequired && requestId ? (
              <div className="space-y-4">
                <p>Sign in to review this connection request.</p>
                <Link
                  className="inline-flex min-h-11 items-center justify-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-slate-50 hover:bg-slate-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                  to={oauthLoginPath(requestId)}
                >
                  Sign in to Valvo
                </Link>
              </div>
            ) : (
              <>
                {error && (
                  <div role="alert" className="space-y-3 text-sm text-red-700">
                    <p>{error}</p>
                    {requestId && (
                      <Button variant="outline" onClick={() => void load()}>
                        Reload request
                      </Button>
                    )}
                  </div>
                )}
                {request && (
                  <>
                    <div className="space-y-2">
                      <h2 className="break-words text-lg font-semibold text-gray-900">
                        {request.clientName}
                      </h2>
                      <p className="text-sm text-gray-600">
                        This name was supplied by the client and is not verified by Valvo. Only
                        allow a connection you started and trust.
                      </p>
                    </div>
                    {request.organizations.length === 0 ? (
                      <output>
                        You have no organizations available. Ask an organization administrator for
                        access, then restart the connection.
                      </output>
                    ) : (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="consent-organization">Organization</Label>
                          <select
                            id="consent-organization"
                            value={organizationId}
                            onChange={(event) => setOrganizationId(event.target.value)}
                            disabled={submitting}
                            className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-2 focus-visible:outline-blue-600"
                          >
                            <option value="">Choose an organization</option>
                            {request.organizations.map((organization) => (
                              <option key={organization.id} value={organization.id}>
                                {organization.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-sm text-gray-600">
                            Access stays with this organization until you disconnect.
                          </p>
                        </div>
                        <fieldset disabled={submitting} className="space-y-3">
                          <legend className="mb-2 text-sm font-medium">Access to allow</legend>
                          <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
                            <input
                              type="radio"
                              name="access"
                              value="read"
                              checked={access === 'read'}
                              onChange={() => setAccess('read')}
                              className="mt-1 accent-blue-600"
                            />
                            <span>
                              <span className="block font-medium">Read only</span>
                              <span className="block text-sm text-gray-600">
                                View monitors, check results and reported jobs.
                              </span>
                            </span>
                          </label>
                          {request.requestedScopes.includes('monitors:write') && (
                            <label className="flex items-start gap-3 rounded-md border p-3 cursor-pointer">
                              <input
                                type="radio"
                                name="access"
                                value="write"
                                checked={access === 'write'}
                                onChange={() => setAccess('write')}
                                className="mt-1 accent-blue-600"
                              />
                              <span>
                                <span className="block font-medium">Read and write</span>
                                <span className="block text-sm text-gray-600">
                                  Also create and change monitors, pause or resume them, and request
                                  checks.
                                </span>
                              </span>
                            </label>
                          )}
                        </fieldset>
                      </>
                    )}
                    <p className="text-sm text-gray-600">
                      You can disconnect this client in Connected clients at any time.
                    </p>
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                      <Button
                        variant="outline"
                        disabled={submitting}
                        onClick={() => void decide('deny')}
                      >
                        Deny
                      </Button>
                      <Button
                        disabled={submitting || !organizationId || !access}
                        onClick={() => void decide('approve')}
                      >
                        {submitting ? 'Submitting decision…' : 'Allow access'}
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
